import React, { useState, useEffect } from 'react';
import adminApi from '../../services/adminApi';
import NotificationContainer from '../Notification/NotificationContainer';

const AdminOrders = ({ filter }) => {
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDeliveryPersonModal, setShowDeliveryPersonModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [deliveryPersons, setDeliveryPersons] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [filters, setFilters] = useState({
    search: '',
    status: filter === 'completed' ? 'delivered' : (filter || 'all') // Convertir 'completed' en 'delivered'
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);
  const [sortField, setSortField] = useState(null);
  const [sortOrder, setSortOrder] = useState('desc');
  const [newStatus, setNewStatus] = useState('');
  const [selectedDeliveryPersonId, setSelectedDeliveryPersonId] = useState('');

  // Système de notifications
  const addNotification = (message, type = 'success') => {
    const id = Date.now() + Math.random();
    setNotifications(prev => [...prev, { id, message, type }]);
    return id;
  };

  const removeNotification = (id) => {
    setNotifications(prev => prev.filter(n => n.id !== id));
  };

  // Charger les données au montage du composant
  useEffect(() => {
    loadData();
    loadDeliveryPersons();
  }, []);

  const loadDeliveryPersons = async () => {
    try {
      const deliveryData = await adminApi.getDeliveryPersons();
      const deliveryList = Array.isArray(deliveryData) ? deliveryData : (deliveryData?.data || []);
      setDeliveryPersons(deliveryList);
    } catch (error) {
      console.error('Erreur lors du chargement des livreurs:', error);
    }
  };

  // Appliquer le filtre initial si fourni - réinitialiser quand le filtre change
  useEffect(() => {
    const newStatus = filter ? (filter === 'completed' ? 'delivered' : filter) : 'all';
    setFilters(prev => ({
      ...prev,
      status: newStatus
    }));
    // Réinitialiser la page courante
    setCurrentPage(1);
  }, [filter]);

  // Fonction pour formater les prix avec séparateurs de milliers
  const formatPrice = (price) => {
    if (!price && price !== 0) return '0';
    const numPrice = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(numPrice)) return '0';
    return numPrice.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  };

  // Fonction pour formater les dates
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Fonction pour obtenir le badge de statut
  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { label: 'En attente', class: 'badge-warning' },
      confirmed: { label: 'Confirmée', class: 'badge-info' },
      processing: { label: 'En traitement', class: 'badge-primary' },
      shipped: { label: 'Expédiée', class: 'badge-primary' },
      delivered: { label: 'Livrée', class: 'badge-success' },
      cancelled: { label: 'Annulée', class: 'badge-danger' },
      refunded: { label: 'Remboursée', class: 'badge-secondary' }
    };
    const statusInfo = statusMap[status] || { label: status, class: 'badge-secondary' };
    return <span className={`badge ${statusInfo.class}`}>{statusInfo.label}</span>;
  };

  // Fonction pour gérer le tri
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  // Appliquer les filtres et le tri
  useEffect(() => {
    let filtered = orders;

    if (filters.search) {
      filtered = filtered.filter(order =>
        order.order_number?.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.user?.name?.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.user?.email?.toLowerCase().includes(filters.search.toLowerCase()) ||
        order.delivery_name?.toLowerCase().includes(filters.search.toLowerCase())
      );
    }

    if (filters.status && filters.status !== 'all') {
      filtered = filtered.filter(order => order.status === filters.status);
    }

    // Appliquer le tri
    if (sortField) {
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;

        switch (sortField) {
          case 'order_number':
            aValue = (a.order_number || '').toLowerCase();
            bValue = (b.order_number || '').toLowerCase();
            break;
          case 'customer':
            aValue = (a.user?.name || a.delivery_name || '').toLowerCase();
            bValue = (b.user?.name || b.delivery_name || '').toLowerCase();
            break;
          case 'total':
            aValue = parseFloat(a.total_amount) || 0;
            bValue = parseFloat(b.total_amount) || 0;
            break;
          case 'status':
            aValue = a.status || '';
            bValue = b.status || '';
            break;
          case 'date':
            aValue = new Date(a.created_at).getTime();
            bValue = new Date(b.created_at).getTime();
            break;
          default:
            return 0;
        }

        if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
        return 0;
      });
    }

    setFilteredOrders(filtered);
    setCurrentPage(1);
  }, [orders, filters, sortField, sortOrder]);

  const loadData = async () => {
    try {
      setLoading(true);
      const ordersData = await adminApi.getOrders('all');
      // La réponse peut être directement un tableau ou dans data
      const ordersList = Array.isArray(ordersData) ? ordersData : (ordersData?.data || []);
      setOrders(ordersList);
    } catch (error) {
      console.error('Erreur lors du chargement des commandes:', error);
      addNotification('Erreur lors du chargement des commandes', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const clearFilters = () => {
    setFilters({
      search: '',
      status: 'all'
    });
  };

  const handleViewDetails = (order) => {
    setSelectedOrder(order);
    setShowDetailsModal(true);
  };

  const handleStatusChange = (order) => {
    setSelectedOrder(order);
    setNewStatus(order.status);
    setShowStatusModal(true);
  };

  const handleAssignDeliveryPerson = (order) => {
    setSelectedOrder(order);
    setSelectedDeliveryPersonId(order.delivery_person_id || '');
    setShowDeliveryPersonModal(true);
  };

  const handleUpdateDeliveryPerson = async () => {
    if (!selectedOrder) return;

    try {
      const response = await adminApi.assignDeliveryPerson(selectedOrder.id, selectedDeliveryPersonId);
      const updatedOrder = response?.data || response;

      setOrders(prev => prev.map(order =>
        order.id === selectedOrder.id ? updatedOrder : order
      ));

      setShowDeliveryPersonModal(false);
      setSelectedOrder(null);
      setSelectedDeliveryPersonId('');
      addNotification('Livreur assigné avec succès', 'success');
      await loadData(); // Recharger pour avoir les données complètes
    } catch (error) {
      console.error('Erreur lors de l\'assignation du livreur:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de l\'assignation';
      addNotification(errorMessage, 'error');
    }
  };

  const handleUpdateStatus = async () => {
    if (!selectedOrder || !newStatus) return;

    try {
      const response = await adminApi.updateOrderStatus(selectedOrder.id, newStatus);
      const updatedOrder = response?.data || response;

      setOrders(prev => prev.map(order =>
        order.id === selectedOrder.id ? updatedOrder : order
      ));

      setShowStatusModal(false);
      setSelectedOrder(null);
      addNotification('Statut de la commande mis à jour avec succès', 'success');
      await loadData(); // Recharger pour avoir les données complètes
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Erreur lors de la mise à jour';
      addNotification(errorMessage, 'error');
    }
  };

  // Fonction pour exporter les commandes en CSV
  const handleExport = () => {
    try {
      const ordersToExport = filteredOrders.length > 0 ? filteredOrders : orders;

      if (ordersToExport.length === 0) {
        addNotification('Aucune commande à exporter', 'warning');
        return;
      }

      const headers = [
        'Numéro de commande',
        'Client',
        'Email',
        'Téléphone',
        'Adresse de livraison',
        'Montant total (FCFA)',
        'Statut',
        'Méthode de paiement',
        'Date de création'
      ];

      const csvRows = ordersToExport.map(order => {
        const row = [
          order.order_number || '',
          `"${(order.user?.name || order.delivery_name || '').replace(/"/g, '""')}"`,
          order.user?.email || order.delivery_phone || '',
          order.delivery_phone || '',
          `"${(order.delivery_address || '').replace(/"/g, '""')}"`,
          formatPrice(order.total_amount),
          order.status || '',
          order.payment_method || '',
          order.created_at ? new Date(order.created_at).toLocaleDateString('fr-FR') : ''
        ];
        return row.join(',');
      });

      const csvContent = [
        headers.join(','),
        ...csvRows
      ].join('\n');

      const BOM = '\uFEFF';
      const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });

      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);

      const date = new Date().toISOString().split('T')[0];
      const filename = `commandes_${date}.csv`;
      link.setAttribute('download', filename);

      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      addNotification(`${ordersToExport.length} commande(s) exportée(s) avec succès`, 'success');
    } catch (error) {
      console.error('Erreur lors de l\'export:', error);
      addNotification('Erreur lors de l\'export des commandes', 'error');
    }
  };

  // Pagination
  const totalPages = Math.ceil(filteredOrders.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentOrders = filteredOrders.slice(startIndex, endIndex);

  if (loading) {
    return (
      <div className="fade-in">
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            <p className="mt-4 text-gray-600">Chargement des commandes...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fade-in">
      <NotificationContainer notifications={notifications} onRemove={removeNotification} />

      <div className="breadcrumb">
        <a href="/admin" className="breadcrumb-item">Dashboard</a>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Gestion</span>
        <span className="breadcrumb-separator">/</span>
        <span className="breadcrumb-item">Commandes</span>
      </div>

      <div className="page-header">
        <h1 className="page-title">Gestion des Commandes</h1>
        <p className="page-subtitle">
          Suivre et gérer toutes les commandes du marché agricole
        </p>
      </div>

      {/* Filtres et Actions */}
      <div className="mb-6 flex flex-wrap items-center gap-4">
        <div className="flex flex-wrap gap-3 flex-1">
          {/* Recherche */}
          <input
            type="text"
            name="search"
            placeholder="Rechercher une commande..."
            value={filters.search}
            onChange={handleFilterChange}
            className="search-input"
          />

          {/* Filtre par statut */}
          <select
            name="status"
            value={filters.status}
            onChange={handleFilterChange}
            className="search-input"
          >
            <option value="all">Tous les statuts</option>
            <option value="pending">En attente</option>
            <option value="confirmed">Confirmée</option>
            <option value="processing">En traitement</option>
            <option value="shipped">Expédiée</option>
            <option value="delivered">Livrée</option>
            <option value="cancelled">Annulée</option>
            <option value="refunded">Remboursée</option>
          </select>

          {/* Bouton pour effacer les filtres */}
          <button
            onClick={clearFilters}
            className="btn btn-secondary text-sm"
          >
            Effacer
          </button>
        </div>
      </div>

      {/* Orders DataTable */}
      <div className="table-container">
        <div className="table-header">
          <h3 className="table-title">
            Liste des Commandes
            <span className="text-sm text-gray-500 ml-2">
              ({filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''})
            </span>
          </h3>
          <div className="table-actions">
            <button
              onClick={handleExport}
              className="btn btn-secondary"
              disabled={filteredOrders.length === 0}
            >
              <svg className="w-4 h-4 mr-2 inline" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              Exporter CSV
            </button>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>
                  <button
                    onClick={() => handleSort('order_number')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    N° Commande
                    {sortField === 'order_number' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('customer')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Client
                    {sortField === 'customer' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Contact</th>
                <th>Livreur</th>
                <th>
                  <button
                    onClick={() => handleSort('total')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Montant
                    {sortField === 'total' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('status')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Statut
                    {sortField === 'status' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>
                  <button
                    onClick={() => handleSort('date')}
                    className="flex items-center gap-2 hover:text-green-600 transition-colors"
                    style={{ background: 'none', border: 'none', padding: 0, cursor: 'pointer', fontWeight: 'inherit', color: 'inherit' }}
                  >
                    Date
                    {sortField === 'date' && (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        {sortOrder === 'asc' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 15l7-7 7 7" />
                        ) : (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        )}
                      </svg>
                    )}
                  </button>
                </th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {currentOrders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="text-center py-8 text-gray-500">
                    <div className="flex flex-col items-center">
                      <svg className="w-12 h-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
                      </svg>
                      <p className="text-lg font-medium">Aucune commande trouvée</p>
                      <p className="text-sm">Les commandes apparaîtront ici</p>
                    </div>
                  </td>
                </tr>
              ) : (
                currentOrders.map((order) => (
                  <tr key={order.id}>
                    <td className="font-medium">
                      <span className="text-blue-600">#{order.order_number || order.id}</span>
                    </td>
                    <td>
                      <div>
                        <div className="font-medium text-gray-900 dark:text-white">
                          {order.user?.name || order.delivery_name || 'Client inconnu'}
                        </div>
                        {order.user?.email && (
                          <div className="text-sm text-gray-500 dark:text-gray-400">
                            {order.user.email}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        <div>{order.delivery_phone || order.user?.phone || 'N/A'}</div>
                        {order.delivery_address && (
                          <div className="text-gray-500 truncate max-w-xs">
                            {order.delivery_address}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="text-sm">
                        {order.delivery_person?.name || order.deliveryPerson?.name ? (
                          <div className="flex items-center gap-2">
                            <svg className="w-4 h-4 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            <span className="font-medium text-gray-900">
                              {order.delivery_person?.name || order.deliveryPerson?.name}
                            </span>
                          </div>
                        ) : (
                          <span className="text-gray-400 italic">Non assigné</span>
                        )}
                        {order.delivery_person?.phone && (
                          <div className="text-xs text-gray-500 mt-1">
                            {order.delivery_person.phone}
                          </div>
                        )}
                      </div>
                    </td>
                    <td>
                      <div className="font-medium text-green-600">
                        {formatPrice(order.total_amount)} FCFA
                      </div>
                      {order.payment_method && (
                        <div className="text-xs text-gray-500">
                          {order.payment_method === 'cash_on_delivery' ? 'Paiement à la livraison' :
                           order.payment_method === 'mobile_money' ? 'Mobile Money' :
                           order.payment_method}
                        </div>
                      )}
                    </td>
                    <td>
                      {getStatusBadge(order.status)}
                    </td>
                    <td className="text-sm text-gray-500">
                      {formatDate(order.created_at)}
                    </td>
                    <td>
                      <div className="flex items-center gap-2 flex-wrap">
                        {/* Bouton Détails - Bleu/Info */}
                        <button
                          onClick={() => handleViewDetails(order)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{ backgroundColor: '#3b82f6' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#2563eb'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#3b82f6'}
                          title="Voir les détails"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          Détails
                        </button>

                        {/* Bouton Modifier le statut - Jaune */}
                        <button
                          onClick={() => handleStatusChange(order)}
                          className="inline-flex items-center px-3 py-1.5 text-sm font-medium text-white rounded-lg transition-colors"
                          style={{ backgroundColor: '#eab308' }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#ca8a04'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#eab308'}
                          title="Modifier le statut"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                          Statut
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between mt-4 px-4 py-3">
            <div className="text-sm text-gray-700">
              Affichage de {startIndex + 1} à {Math.min(endIndex, filteredOrders.length)} sur {filteredOrders.length} commande{filteredOrders.length > 1 ? 's' : ''}
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                disabled={currentPage === 1}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Précédent
              </button>
              <span className="flex items-center px-4 text-sm text-gray-700">
                Page {currentPage} sur {totalPages}
              </span>
              <button
                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                disabled={currentPage === totalPages}
                className="btn btn-secondary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Suivant
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {showDetailsModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content max-w-4xl" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Détails de la Commande #{selectedOrder.order_number || selectedOrder.id}</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDetailsModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Informations client */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-gray-900 mb-4">Informations Client</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Nom</label>
                    <p className="text-gray-900">{selectedOrder.user?.name || selectedOrder.delivery_name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Email</label>
                    <p className="text-gray-900">{selectedOrder.user?.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Téléphone</label>
                    <p className="text-gray-900">{selectedOrder.delivery_phone || selectedOrder.user?.phone || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Adresse de livraison</label>
                    <p className="text-gray-900">{selectedOrder.delivery_address || 'N/A'}</p>
                    {selectedOrder.delivery_city && (
                      <p className="text-sm text-gray-500">{selectedOrder.delivery_city}, {selectedOrder.delivery_region}</p>
                    )}
                  </div>
                </div>

                {/* Informations commande */}
                <div className="space-y-4">
                  <h4 className="font-semibold text-lg text-gray-900 mb-4">Informations Commande</h4>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Statut</label>
                    <p>{getStatusBadge(selectedOrder.status)}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-500">Date de création</label>
                    <p className="text-gray-900">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  {selectedOrder.delivered_at && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Date de livraison</label>
                      <p className="text-gray-900">{formatDate(selectedOrder.delivered_at)}</p>
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-gray-500">Méthode de paiement</label>
                    <p className="text-gray-900">
                      {selectedOrder.payment_method === 'cash_on_delivery' ? 'Paiement à la livraison' :
                       selectedOrder.payment_method === 'mobile_money' ? 'Mobile Money' :
                       selectedOrder.payment_method || 'N/A'}
                    </p>
                  </div>
                  {selectedOrder.delivery_notes && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Notes de livraison</label>
                      <p className="text-gray-900">{selectedOrder.delivery_notes}</p>
                    </div>
                  )}
                  {selectedOrder.status === 'cancelled' && selectedOrder.cancellation_comment && (
                    <div>
                      <label className="text-sm font-medium text-gray-500">Commentaire d'annulation</label>
                      <p className="text-gray-900 bg-red-50 p-3 rounded border border-red-200">
                        {selectedOrder.cancellation_comment}
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* Articles de la commande */}
              <div className="mt-6 border-t pt-6">
                <h4 className="font-semibold text-lg text-gray-900 mb-4">Articles commandés</h4>
                {selectedOrder.items && selectedOrder.items.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b">
                          <th className="text-left py-2">Produit</th>
                          <th className="text-right py-2">Quantité</th>
                          <th className="text-right py-2">Prix unitaire</th>
                          <th className="text-right py-2">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, index) => {
                          const unitPrice = item.unit_price || item.price || 0;
                          const totalPrice = item.total_price || (item.quantity * unitPrice);
                          return (
                            <tr key={item.id || index} className="border-b">
                              <td className="py-2">{item.product?.name || item.product_name || 'Produit supprimé'}</td>
                              <td className="text-right py-2">{item.quantity}</td>
                              <td className="text-right py-2">{formatPrice(unitPrice)} FCFA</td>
                              <td className="text-right py-2 font-medium">{formatPrice(totalPrice)} FCFA</td>
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        <tr className="border-t-2 font-semibold">
                          <td colSpan="3" className="text-right py-2">Sous-total</td>
                          <td className="text-right py-2">{formatPrice(selectedOrder.subtotal)} FCFA</td>
                        </tr>
                        {selectedOrder.delivery_fee > 0 && (
                          <tr>
                            <td colSpan="3" className="text-right py-2">Frais de livraison</td>
                            <td className="text-right py-2">{formatPrice(selectedOrder.delivery_fee)} FCFA</td>
                          </tr>
                        )}
                        {selectedOrder.tax_amount > 0 && (
                          <tr>
                            <td colSpan="3" className="text-right py-2">Taxe</td>
                            <td className="text-right py-2">{formatPrice(selectedOrder.tax_amount)} FCFA</td>
                          </tr>
                        )}
                        {selectedOrder.discount_amount > 0 && (
                          <tr>
                            <td colSpan="3" className="text-right py-2">Réduction</td>
                            <td className="text-right py-2 text-green-600">-{formatPrice(selectedOrder.discount_amount)} FCFA</td>
                          </tr>
                        )}
                        <tr className="border-t-2 font-bold text-lg">
                          <td colSpan="3" className="text-right py-2">Total</td>
                          <td className="text-right py-2 text-green-600">{formatPrice(selectedOrder.total_amount)} FCFA</td>
                        </tr>
                      </tfoot>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500">Aucun article trouvé</p>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="btn btn-secondary"
              >
                Fermer
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleStatusChange(selectedOrder);
                }}
                className="btn btn-primary"
              >
                Modifier le statut
              </button>
              <button
                onClick={() => {
                  setShowDetailsModal(false);
                  handleAssignDeliveryPerson(selectedOrder);
                }}
                className="btn"
                style={{ backgroundColor: '#10b981', color: 'white' }}
              >
                Assigner un livreur
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal d'assignation de livreur */}
      {showDeliveryPersonModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowDeliveryPersonModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Assigner un livreur</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowDeliveryPersonModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Commande #{selectedOrder.order_number || selectedOrder.id}
                  </label>
                  <p className="text-gray-700 mb-4">
                    {selectedOrder.delivery_address || 'N/A'}
                  </p>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Livreur</label>
                  <select
                    value={selectedDeliveryPersonId}
                    onChange={(e) => setSelectedDeliveryPersonId(e.target.value)}
                    className="modal-form-select"
                  >
                    <option value="">Sélectionner un livreur</option>
                    {deliveryPersons.map((person) => (
                      <option key={person.id} value={person.id}>
                        {person.name} {person.phone ? `- ${person.phone}` : ''}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowDeliveryPersonModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateDeliveryPerson}
                className="btn btn-primary"
                disabled={!selectedDeliveryPersonId}
              >
                Assigner
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de modification de statut */}
      {showStatusModal && selectedOrder && (
        <div className="modal-overlay" onClick={() => setShowStatusModal(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Modifier le statut</h3>
              <button
                className="text-gray-400 hover:text-gray-600"
                onClick={() => setShowStatusModal(false)}
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            <div className="modal-body">
              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium text-gray-500 mb-2 block">
                    Commande #{selectedOrder.order_number || selectedOrder.id}
                  </label>
                  <p className="text-gray-700 mb-4">
                    Statut actuel : {getStatusBadge(selectedOrder.status)}
                  </p>
                </div>

                <div className="modal-form-group">
                  <label className="modal-form-label">Nouveau statut</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="modal-form-select"
                  >
                    <option value="pending">En attente</option>
                    <option value="confirmed">Confirmée</option>
                    <option value="processing">En traitement</option>
                    <option value="shipped">Expédiée</option>
                    <option value="delivered">Livrée</option>
                    <option value="cancelled">Annulée</option>
                    <option value="refunded">Remboursée</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="modal-footer">
              <button
                onClick={() => setShowStatusModal(false)}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button
                onClick={handleUpdateStatus}
                className="btn btn-primary"
                disabled={!newStatus || newStatus === selectedOrder.status}
              >
                Mettre à jour
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminOrders;
